import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
// import { formatCurrency } from 'common/src/utils/functions';
import { formatPrice as formatCurrency } from 'common/src/utils/functions';
import { metrics__get_tasks_per_commune_per_sex } from 'common/src/api/metrics';

interface SexData {
  sex: string;
  commune_data: Record<string, number>;
  total_tasks: number;
  total_revenue: number;
  revenue_percentage: number;
}

const TasksPerCommunePerSexReport: React.FC = () => {
  const [sexData, setSexData] = useState<SexData[]>([]);
  const [startDate, setStartDate] = useState('2023-01-01');
  const [endDate, setEndDate] = useState('2024-12-31');
  const [communes, setCommunes] = useState<string[]>([]);

  const fetchTasksPerCommunePerSex = async (
    startDate: string,
    endDate: string,
  ): Promise<SexData[]> => {
        // TODO extraer todo los servicios de supabase
    const { data, error } = await metrics__get_tasks_per_commune_per_sex(startDate, endDate);

    if (error) {
      console.error('Error fetching tasks per commune per sex:', error);
      throw error;
    }

    console.log('Raw data from SQL function:', JSON.stringify(data, null, 2));
    return data || [];
  };

  const loadSexData = async () => {
    try {
      const data = await fetchTasksPerCommunePerSex(startDate, endDate);
      console.log('Processed data:', JSON.stringify(data, null, 2));
      // Filter out the "Total" row from the main data
      const filteredData = data.filter((item) => item.sex !== 'Total');
      setSexData(filteredData);
      if (filteredData.length > 0 && filteredData[0].commune_data) {
        setCommunes(Object.keys(filteredData[0].commune_data).sort());
      }
    } catch (error) {
      console.error('Error loading sex data:', error);
      // Handle error (e.g., show an error message to the user)
    }
  };

  useEffect(() => {
    loadSexData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const exportToExcel = () => {
    const fileType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    const ws = XLSX.utils.json_to_sheet(
      sexData.map((data) => ({
        Sexo: data.sex,
        ...data.commune_data,
        'Total de Tareas': data.total_tasks,
        'Ingresos Totales': formatCurrency(data.total_revenue),
        '% de Ingresos': `${data.revenue_percentage.toFixed(2)}%`,
      })),
    );

    const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], { type: fileType });
    saveAs(
      dataBlob,
      `tasks_per_commune_per_sex_${startDate}_${endDate}${fileExtension}`,
    );
  };

  const getHeatmapColor = (value: number, max: number) => {
    const intensity = Math.min((value / max) * 100, 100);
    return `bg-blue-${Math.round(intensity / 10) * 100}`;
  };

  const maxTasks = useMemo(() => {
    return Math.max(
      ...sexData.flatMap((data) => Object.values(data.commune_data)),
    );
  }, [sexData]);

  const columnTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    communes.forEach((commune) => {
      totals[commune] = sexData.reduce(
        (sum, data) => sum + (data.commune_data[commune] || 0),
        0,
      );
    });
    return totals;
  }, [sexData, communes]);

  const grandTotal = useMemo(() => {
    return sexData.reduce((sum, data) => sum + data.total_tasks, 0);
  }, [sexData]);

  const grandTotalRevenue = useMemo(() => {
    return sexData.reduce((sum, data) => sum + data.total_revenue, 0);
  }, [sexData]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between p-4 bg-white border-b shadow-sm">
        <div className="w-96">
          <h3 className="text-lg font-semibold mb-3">
            Seleccionar Rango de Fechas
          </h3>
          <div className="space-y-4">
            <div>
              <label
                htmlFor="startDate"
                className="block text-sm font-medium text-gray-700"
              >
                Fecha de Inicio
              </label>
              <input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
            </div>
            <div>
              <label
                htmlFor="endDate"
                className="block text-sm font-medium text-gray-700"
              >
                Fecha de Fin
              </label>
              <input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
            </div>
            <button
              onClick={loadSexData}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Aplicar Rango
            </button>
          </div>
        </div>

        <div className="w-96">
          <h3 className="text-lg font-semibold mb-3">Otras Opciones</h3>
          <button
            onClick={exportToExcel}
            className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Exportar a Excel
          </button>
        </div>
      </div>

      <div className="flex-grow overflow-auto">
        <div className="inline-block min-w-full">
          <table className="min-w-full bg-white">
            <thead className="sticky top-0 bg-gray-200 text-gray-600 uppercase text-sm leading-normal">
              <tr>
                <th className="py-3 px-6 text-left">Sexo</th>
                {communes.map((commune) => (
                  <th key={commune} className="py-3 px-6 text-center">
                    {commune}
                  </th>
                ))}
                <th className="py-3 px-6 text-center">Total de Tareas</th>
                <th className="py-3 px-6 text-center">Ingresos Totales</th>
                <th className="py-3 px-6 text-center">% de Ingresos</th>
              </tr>
            </thead>
            <tbody className="text-gray-600 text-sm font-light">
              {sexData.map((data) => (
                <tr
                  key={data.sex}
                  className="border-b border-gray-200 hover:bg-gray-100"
                >
                  <td className="py-3 px-6 text-left whitespace-nowrap">
                    {data.sex}
                  </td>
                  {communes.map((commune) => (
                    <td
                      key={commune}
                      className={`py-3 px-6 text-center ${getHeatmapColor(
                        data.commune_data[commune] || 0,
                        maxTasks,
                      )}`}
                    >
                      {data.commune_data[commune] || 0}
                    </td>
                  ))}
                  <td className="py-3 px-6 text-center">{data.total_tasks}</td>
                  <td className="py-3 px-6 text-center">
                    {formatCurrency(data.total_revenue)}
                  </td>
                  <td className="py-3 px-6 text-center">
                    {data.revenue_percentage.toFixed(2)}%
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-100 font-bold">
                <td className="py-3 px-6 text-left whitespace-nowrap">Total</td>
                {communes.map((commune) => (
                  <td key={commune} className="py-3 px-6 text-center">
                    {columnTotals[commune]}
                  </td>
                ))}
                <td className="py-3 px-6 text-center">{grandTotal}</td>
                <td className="py-3 px-6 text-center">
                  {formatCurrency(grandTotalRevenue)}
                </td>
                <td className="py-3 px-6 text-center">100.00%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TasksPerCommunePerSexReport;
